import React, { useEffect } from 'react';
import 'regenerator-runtime/runtime';
import { Route, Switch } from 'react-router-dom';
import { onCLS, onLCP, onFCP, onINP, onTTFB, onFID } from 'web-vitals/attribution';
import './styles/base.css';
import './styles/layout.css';
import './styles/responsive.css';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import * as metadata from './metadata';
import './styles/mainDesktop.css';
import './styles/mainMobile.css';
import Header from './Header';
import { addToMetricQueue, flushMetricQueue } from './webVitals';
import delayedScheduler from './utils/scheduler';

const LiveTrainStatus = loadable(() => import('./pages/LiveTrainStatus/LiveTrainStatus'));

const RouteInfo = loadable(() => import('./pages/route_info/RouteInfo'));
const TrainInfo = loadable(() => import('./pages/train_info/TrainInfo'));
const StationInfo = loadable(() => import('./pages/station_info/StationInfo'));
const TrainTypeList = loadable(() => import('./pages/train_type_list/TrainTypeList'));
const StaticReservationCancellation = loadable(() =>
  import('./pages/static_reservation_cancellation/StaticReservationCancellation')
);
const StaticListIndex = loadable(() => import('./pages/static_list_index/StaticListIndex'));
const StaticListIndexSingle = loadable(() => import('../src/pages/static_list_index_single/StaticListIndexSingle'));
const StaticTatkalPage = loadable(() => import('./pages/TatkalPage/TatkalPage'));
const StaticCovidEpassPage = loadable(() => import('./pages/CovidSpecialTrainsPage/CovidSpecialTrainsPage'));
const StaticCovidInsurancePage = loadable(() => import('./pages/CovidInsurancePage/StaticCovidInsurancePage'));
const FaqPage = loadable(() => import('./pages/FaqPage/FaqPage'));

const RescheduledTrains = loadable(() => import('./pages/UpdateCenter/RescheduledTrains'));
const DivertedTrains = loadable(() => import('./pages/UpdateCenter/DivertedTrains'));
const CancelledTrains = loadable(() => import('./pages/UpdateCenter/CancelledTrains'));
const StaticFestiveTrainsPage = loadable(() => import('./pages/FestiveSpecialTrainsPage/FestiveSpecialTrainsPage'));
const StaticKumbhMelaTrainsPage = loadable(() =>
  import('./pages/KumbhMelaSpecialTrainsPage/KumbhMelaSpecialTrainsPage')
);
const StaticKumbhMelaPage = loadable(() => import('./pages/KumbhMelaPage/KumbhMelaPage'));

const FaqTopicPage = loadable(() => import('../src/pages/FaqTopicPage/FaqTopicPage'));

const StaticIrctcFaqHomePage = loadable(() => import('./pages/IrctcFaq/components/questionPage/questionPage'));

const FoodOrderingPage = loadable(() => import('./pages/FoodOrderingPage/FoodOrderingPage'));

const IrctcCreateAccountPage = loadable(() => import('./pages/IrctcCreateAccountPage/IrctcCreateAccountPage'));

const IrctcForgotUserNamePage = loadable(() => import('./pages/IrctcForgotUserNamePage/IrctcForgotUserNamePage'));

const IrctcForgotPasswordPage = loadable(() => import('./pages/IrctcForgotPasswordPage/IrctcForgotPasswordPage'));

const IrctcLoginPage = loadable(() => import('./pages/IrctcLoginPage/IrctcLoginPage'));

const PnrSeoPage = loadable(() => import('./pages/PnrSeo/PnrSeoPage'));

const RailsLandingSeo = loadable(() => import('./pages/RailsLandingSeo/RailsLandingSeo'));

const LTSRoutesWrapper = loadable(() => import('./pages/LTS/pages/LTSRoutesWrapper/LTSRoutesWrapper'));

require('isomorphic-fetch');

const App = ({ data }) => {
  const {
    pageUrl,
    template: { template_name }
  } = data;

  const pageInfo = { pageUrl, template_name };

  useEffect(() => {
    const metricQueue = new Set();

    const webVitalMetricsCallback = (metric) => {
      addToMetricQueue(metric, metricQueue);
    };

    const handlePageHide = () => flushMetricQueue(metricQueue, pageInfo);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushMetricQueue(metricQueue, pageInfo);
      }
    };

    // Adding delayScheduler to window.schedulerDelay
    if (typeof window !== 'undefined') {
      window.schedulerDelay = delayedScheduler;
    }

    addEventListener('visibilitychange', handleVisibilityChange);
    addEventListener('pagehide', handlePageHide);

    onCLS(webVitalMetricsCallback, { reportAllChanges: true });
    onLCP(webVitalMetricsCallback, { reportAllChanges: true });
    onFCP(webVitalMetricsCallback, { reportAllChanges: true });
    onINP(webVitalMetricsCallback, { reportAllChanges: true });
    onFID(webVitalMetricsCallback, { reportAllChanges: true });
    onTTFB(webVitalMetricsCallback, { reportAllChanges: true });

    return () => {
      removeEventListener('visibilitychange', handleVisibilityChange);
      removeEventListener('pagehide', handlePageHide);
    };
  }, []);
  return (
    <div style={{ width: '100%' }}>
      <div className={'mmt-header'}>
        <Header />
      </div>
      {data.template.meta_data ? (
        <Helmet
          title={data.template.meta_data.title}
          meta={data.template.meta_data.meta}
          link={data.template.meta_data.link}
          script={data.template.meta_data.script}
          noscript={data.template.meta_data.noscript}
        />
      ) : (
        <Helmet
          title={metadata.title}
          meta={metadata.meta}
          link={metadata.link}
          script={metadata.script}
          noscript={metadata.noscript}
        />
      )}

      <div className="backGroundGray">
        <Switch>
          {/*Irctc Login Live SEO page*/}
          <Route
            exact
            path="/railways/irctc-login.html"
            render={(props) => <IrctcLoginPage data={data} {...props} />}
          />
          {/*Irctc Forgot Username Live SEO page*/}
          <Route
            exact
            path="/railways/irctc-forgot-username.html"
            render={(props) => <IrctcForgotUserNamePage data={data} {...props} />}
          />
          {/*Irctc Create Account Live SEO page*/}
          <Route
            exact
            path="/railways/irctc-create-account.html"
            render={(props) => <IrctcCreateAccountPage data={data} {...props} />}
          />
          {/*Irctc Forgot Passowrd Live SEO page*/}
          <Route
            exact
            path="/railways/irctc-forgot-password.html"
            render={(props) => <IrctcForgotPasswordPage data={data} {...props} />}
          />
          {/*PNR Search Live SEO page*/}
          <Route exact path="/railways/PNR/" render={(props) => <PnrSeoPage data={data} {...props} />} />

          {/* Rails Landing Live SEO page */}
          <Route exact path="/railways/" render={(props) => <RailsLandingSeo data={data} {...props} />} />

          {/*Irctc Login Static HTML page*/}
          <Route
            exact
            path="/staticPage/irctc-login.html"
            render={(props) => <IrctcLoginPage data={data} {...props} />}
          />
          {/*Irctc Forgot Username Static HTML page*/}
          <Route
            exact
            path="/staticPage/irctc-forgot-username.html"
            render={(props) => <IrctcForgotUserNamePage data={data} {...props} />}
          />
          {/*Irctc Create Account Static HTML page*/}
          <Route
            exact
            path="/staticPage/irctc-create-account.html"
            render={(props) => <IrctcCreateAccountPage data={data} {...props} />}
          />
          {/*Irctc Forgot Passowrd Static HTML page*/}
          <Route
            exact
            path="/staticPage/irctc-forgot-password.html"
            render={(props) => <IrctcForgotPasswordPage data={data} {...props} />}
          />
          {/*PNR Search Static HTML page*/}
          <Route exact path="/staticPage/PNR/" render={(props) => <PnrSeoPage data={data} {...props} />} />

          {/* Rails Landing Static HTML Page */}
          <Route exact path="/staticPage/" render={(props) => <RailsLandingSeo data={data} {...props} />} />

          {/*Route page for live SEO*/}
          <Route exact path="/railways/faq/topic-*.html" render={(props) => <FaqTopicPage data={data} {...props} />} />
          {/*Route page for live SEO*/}
          <Route exact path="/railways/faq/*.html" render={(props) => <FaqPage data={data} {...props} />} />
          <Route
            exact
            path="/staticPage/food-ordering.html"
            render={(props) => <FoodOrderingPage data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/food-ordering.html"
            render={(props) => <FoodOrderingPage data={data} {...props} />}
          />
          {/*Route page for page generation*/}
          <Route
            exact
            path="/staticPage/rescheduled-trains.html"
            render={(props) => <RescheduledTrains data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/rescheduled-trains.html"
            render={(props) => <RescheduledTrains data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/diverted-trains.html"
            render={(props) => <DivertedTrains data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/diverted-trains.html"
            render={(props) => <DivertedTrains data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/cancelled-trains.html"
            render={(props) => <CancelledTrains data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/cancelled-trains.html"
            render={(props) => <CancelledTrains data={data} {...props} />}
          />
          {/*Route page for page generation*/}

          <Route
            exact
            path="/staticPage/corona-virus-insurance-policy.html"
            render={(props) => <StaticCovidInsurancePage data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/corona-virus-insurance-policy.html"
            render={(props) => <StaticCovidInsurancePage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/covid-special-trains.html"
            render={(props) => <StaticCovidEpassPage data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/covid-special-trains.html"
            render={(props) => <StaticCovidEpassPage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/festive-special-trains.html"
            render={(props) => <StaticFestiveTrainsPage data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/festive-special-trains.html"
            render={(props) => <StaticFestiveTrainsPage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/kumbh-mela-special-trains.html"
            render={(props) => <StaticKumbhMelaTrainsPage data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/kumbh-mela-special-trains.html"
            render={(props) => <StaticKumbhMelaTrainsPage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/tatkal-railway-reservation.html"
            render={(props) => <StaticTatkalPage data={data} {...props} />}
          />
          {/*Route page for live SEO*/}
          <Route
            exact
            path="/railways/tatkal-railway-reservation.html"
            render={(props) => <StaticTatkalPage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/irctc-faqs.html"
            render={(props) => <StaticIrctcFaqHomePage data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/irctc-faqs.html"
            render={(props) => <StaticIrctcFaqHomePage data={data} {...props} />}
          />
          <Route
            exact
            path="/staticPage/kumbh-mela.html"
            render={(props) => <StaticKumbhMelaPage data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/kumbh-mela.html"
            render={(props) => <StaticKumbhMelaPage data={data} {...props} />}
          />
          <Route
            exact
            path="/trains-between-cities/:fromCity/:toCity"
            render={(props) => <RouteInfo data={data} {...props} />}
          />

          {/*Route page for page fetcher*/}
          <Route
            exact
            path="/tbc/:templateId/:fromCity/:toCity"
            render={(props) => <RouteInfo data={data} {...props} />}
          />

          {/*Route page for page fetcher*/}
          <Route
            exact
            path="/liveTrainStatus/:templateId/:trainNumber"
            render={(props) => <LiveTrainStatus data={data} {...props} />}
          />
          <Route
            exact
            path={`/railways/*-running-status.html`}
            render={(props) => <LiveTrainStatus data={data} {...props} />}
          />
          {/*Train type page for page fetcher*/}
          <Route
            exact
            path="/trainType/:templateId/:trainTypeId"
            render={(props) => <TrainTypeList data={data} {...props} />}
          />
          {/*Train type page for live SEO*/}
          <Route exact path="/railways/*train-list.html" render={(props) => <TrainTypeList data={data} {...props} />} />
          {/*Route page for live SEO*/}
          <Route exact path="/railways/*train-tickets.html" render={(props) => <RouteInfo data={data} {...props} />} />

          {/*Station page for page fetcher*/}
          <Route
            exact
            path="/station/:templateId/:stationId"
            render={(props) => <StationInfo data={data} {...props} />}
          />
          {/*Station page for live SEO*/}
          <Route exact path="/railways/*-station.html" render={(props) => <StationInfo data={data} {...props} />} />

          {/*Train page for page fetcher*/}
          <Route exact path="/train/:templateId/:trainId" render={(props) => <TrainInfo data={data} {...props} />} />
          {/*Train page for live SEO*/}
          <Route exact path="/railways/*-train.html" render={(props) => <TrainInfo data={data} {...props} />} />

          {/*TrainIndex page for page fetcher*/}
          <Route
            exact
            path="/staticPage/list-of-indian-railway-trains.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*TrainIndex page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-indian-railway-trains.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Live train status landing page*/}
          <Route exact path="/staticPage/liveStatus" render={(props) => <LTSRoutesWrapper data={data} {...props} />} />
          <Route exact path="/railways/liveStatus" render={(props) => <LTSRoutesWrapper data={data} {...props} />} />
          {/*Live train status train number search page*/}
          <Route
            exact
            path="/staticPage/train-number"
            render={(props) => <LTSRoutesWrapper data={data} {...props} />}
          />
          <Route exact path="/railways/train-number" render={(props) => <LTSRoutesWrapper data={data} {...props} />} />
          {/*Live train status date picker page*/}
          <Route exact path="/staticPage/date-picker" render={(props) => <LTSRoutesWrapper data={data} {...props} />} />
          <Route exact path="/railways/date-picker" render={(props) => <LTSRoutesWrapper data={data} {...props} />} />
          {/*Live train status station picker page*/}
          <Route
            exact
            path="/staticPage/station-picker"
            render={(props) => <LTSRoutesWrapper data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/station-picker"
            render={(props) => <LTSRoutesWrapper data={data} {...props} />}
          />
          {/*Hindi TrainIndex page for page fetcher*/}
          <Route
            exact
            path="/staticPage/hi/list-of-indian-railway-trains.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Hindi TrainIndex page for live SEO*/}
          <Route
            exact
            path="/railways/hi/list-of-indian-railway-trains.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />

          {/*ReservationCancellation page for page fetcher*/}
          <Route
            exact
            path="/staticPage/railway-reservation-cancellation.html"
            render={(props) => <StaticReservationCancellation data={data} {...props} />}
          />
          {/*ReservationCancellation page for live SEO*/}

          <Route
            exact
            path="/railways/railway-reservation-cancellation.html"
            render={(props) => <StaticReservationCancellation data={data} {...props} />}
          />
          {/*StationIndex page for page fetcher*/}
          <Route
            exact
            path="/staticPage/list-of-indian-railway-stations.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*StationIndex page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-indian-railway-stations.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Hindi StationIndex page for page fetcher*/}
          <Route
            exact
            path="/staticPage/hi/list-of-indian-railway-stations.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Hindi StationIndex page for live SEO*/}
          <Route
            exact
            path="/railways/hi/list-of-indian-railway-stations.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/* Route index page for live page fetcher */}
          <Route
            exact
            path="/staticPage/list-of-indian-railway-routes.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Route Index page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-indian-railway-routes.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Hindi Route index page for live page fetcher */}
          <Route
            exact
            path="/staticPage/hi/list-of-indian-railway-routes.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*Hindi Route Index page for live SEO*/}
          <Route
            exact
            path="/railways/hi/list-of-indian-railway-routes.html"
            render={(props) => <StaticListIndex data={data} {...props} />}
          />
          {/*IndexByLetter of trains and station page for page fetcher*/}
          <Route exact path="/staticPage/:url" render={(props) => <StaticListIndexSingle data={data} {...props} />} />
          {/*Hindi IndexByLetter of trains and station page for page fetcher*/}
          <Route
            exact
            path="/staticPage/hi/:url"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          {/*IndexByLetter of trains page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-indian-railway-*-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          {/*IndexByLetter of station page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-railway-*-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          {/*IndexByLetter of route page for live SEO*/}
          <Route
            exact
            path="/railways/list-of-indian-train-routes-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          <Route
            exact
            path="/railways/hi/list-of-indian-train-routes-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          {/*Hindi IndexByLetter of trains page for live SEO*/}
          <Route
            exact
            path="/railways/hi/list-of-indian-railway-*-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
          {/*Hindi IndexByLetter of station page for live SEO*/}
          <Route
            exact
            path="/railways/hi/list-of-railway-*-by-*.html"
            render={(props) => <StaticListIndexSingle data={data} {...props} />}
          />
        </Switch>
      </div>
    </div>
  );
};
App.propTypes = {
  data: PropTypes.object.isRequired
};

export default App;
